import { useGetList, useNotify, useRecordContext } from "ra-core";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDownIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Contact, OutreachList, OutreachListKind, OutreachListMember } from "../types";
import { useSetOutreachMembership } from "./useSetOutreachMembership";

const NONE_VALUE = "__none__";

/**
 * PRD #16-adjacent — the Offer/Channel split (backlog.md, 2026-09-08).
 * Same dropdown shape as Status/LeadSourceSelector, but the option list comes
 * from real outreach_lists rows (resource "offer" or "channels", both
 * dataProvider-rewritten to outreach_lists filtered by criteria.list_kind —
 * see dataProvider.ts) rather than a fixed enum, since these lists are
 * themselves user-editable (Tony can rename/add/deactivate them from the
 * /offer and /channels pages).
 */
export const ContactOutreachAxisSelector = ({
  kind,
  label,
}: {
  kind: OutreachListKind;
  label: string;
}) => {
  const record = useRecordContext<Contact>();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const contactId = record ? Number(record.id) : undefined;

  const { data: lists } = useGetList<OutreachList>(
    kind === "offer" ? "offer" : "channels",
    { pagination: { page: 1, perPage: 100 }, sort: { field: "name", order: "ASC" } },
  );

  const { data: memberships, isPending: isLoadingMembership } =
    useGetList<OutreachListMember>(
      "outreach_list_members",
      {
        filter: { contact_id: contactId, list_kind: kind, "removed_at@is": null },
        pagination: { page: 1, perPage: 1 },
      },
      { enabled: !!contactId },
    );

  const { mutate: setMembership, isPending: isSaving } =
    useSetOutreachMembership(kind);

  if (!record) return null;

  const currentMembership = memberships?.[0];
  const currentListId = currentMembership?.list_id ?? null;
  const disabled = isLoadingMembership || isSaving || !lists;

  const handleChange = (nextListId: string | number | null) => {
    if (String(nextListId ?? "") === String(currentListId ?? "")) return;
    setMembership(
      {
        contactId: contactId!,
        currentMembershipId: currentMembership?.id ?? null,
        newListId: nextListId,
      },
      {
        onError: (error: any) => {
          notify(error?.message || "ra.notification.http_error", {
            type: "error",
          });
        },
      },
    );
  };

  const options = lists ?? [];
  const selected = options.find((l) => String(l.id) === String(currentListId));

  if (isMobile) {
    return (
      <div className={cn("relative", "w-32")}>
        <div
          aria-hidden="true"
          className={cn(
            "border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs h-9",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          <span className="line-clamp-1">{selected ? selected.name : "None"}</span>
          <ChevronDownIcon className="size-4 opacity-50 shrink-0" />
        </div>
        <select
          disabled={disabled}
          value={currentListId != null ? String(currentListId) : ""}
          onChange={(e) => handleChange(e.target.value || null)}
          aria-label={label}
          className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
        >
          <option value="">None</option>
          {options.map((opt) => (
            <option key={opt.id} value={String(opt.id)}>
              {opt.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <Select
      disabled={disabled}
      value={currentListId != null ? String(currentListId) : NONE_VALUE}
      onValueChange={(value) =>
        handleChange(value === NONE_VALUE ? null : value)
      }
    >
      <SelectTrigger className="w-32">
        <SelectValue placeholder="None" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_VALUE}>None</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.id} value={String(opt.id)}>
            {opt.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
